import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import Reports from '../Reports/Reports';
import { useLocation } from 'react-router-dom';
import type { DateRangeOptionsType, PdfFilterType } from '../types';
import { TimeAssessmentFilterEnum } from '@whizz/api-model';
import { DateTime } from 'luxon';
import { PdfContext } from '../../context/PdfContext';
import { Loader } from '../../components';
import { useI18n } from '@whizz/react-i18n';
import useTitle from '../../hooks/useTitle';

const PdfBuilder: FC = () => {
  const { translate } = useI18n();
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [ search ]);
  const [ pdfIds, setPdfIds ] = useState<number[]>([]);
  const [ pdfFilter, setPdfFilter ] = useState<PdfFilterType>({
    withMathsAge: true,
    withAverages: true,
    withCharts: true,
    classSelectedId: 0,
    dateIntervalSelected: 'full',
    startDate: TimeAssessmentFilterEnum.FIRST_ASSESSMENT,
    endDate: null,
  });
  const [ isPdfReady, setIsPdfReady ] = useState(false);

  useTitle(translate('pages.REPORTS.DOWNLOAD.PDF.title'));

  // Hide the chat and feedback buttons as they are implemented as global components and context cannot be used
  const hideFloatingButtons = () => {
    const feedbackButton = document.getElementById('feedback-button');
    const chatButton = document.getElementById('fc_frame');
    if (null != feedbackButton) feedbackButton.style.display = 'none';
    if (null != chatButton) chatButton.style.display = 'none';
  };

  useEffect(() => {

    const tag = document.getElementsByTagName('html')[0];
    tag?.classList.add('printing');

    const ids = query.get('ids');
    const mathsAge = query.get('maths_age');
    const averages = query.get('class_average');
    const charts = query.get('class_chart');
    const classId = query.get('class_id');
    const dateInterval = query.get('date_interval');
    const startDate = query.get('start_date');
    const endDate = query.get('end_date');

    setPdfIds((ids === null) ? [] : ids.split(',').map((idStr) => parseInt(idStr) || 0));
    setPdfFilter({
      withMathsAge: mathsAge === 'true',
      withAverages: averages === 'true',
      withCharts: charts === 'true',
      classSelectedId: parseInt(classId!),
      dateIntervalSelected: dateInterval as DateRangeOptionsType,
      startDate:
          startDate
            ? Object.values(TimeAssessmentFilterEnum).includes(startDate as TimeAssessmentFilterEnum)
              ? startDate as TimeAssessmentFilterEnum
              : DateTime.fromISO(startDate)
            : TimeAssessmentFilterEnum.FIRST_ASSESSMENT
      ,
      endDate:
          endDate
            ? DateTime.fromISO(endDate)
            : null
      ,
    });
  }, [ search ]);

  useEffect(() => {
    if (isPdfReady) {
      hideFloatingButtons();
      setTimeout(() => {
        window.print();
        setTimeout(window.close, 1);
      }, 2000);
    }
  }, [ isPdfReady ]);

  return (
    <PdfContext.Provider value={{ isPdf: true, pdfIds, setIsPdfReady }}>
      {!isPdfReady &&
       <div className={'my-xl py-xl'}>
         <Loader
           label={translate('pages.REPORTS.DOWNLOAD.MODAL.loader_pdf')}
         />
       </div>
      }
      {pdfFilter.classSelectedId !== 0 ? (
        <div className={`${!isPdfReady && 'invisible'}`}>
          <Reports
            pdfIds={pdfIds}
            pdfFilter={pdfFilter}
          />
        </div>
      ) : null}
    </PdfContext.Provider>
  );
};

export default PdfBuilder;

